/**
 * useKpiDefinitions.ts
 * Fetches KPI definitions from /api/kpi-definitions once, caches at module level.
 * All views share the same cached result — no duplicate fetches.
 */

import { useState, useEffect } from 'react';
import { apiUrl } from './App';

export interface KpiDefinition {
  name: string;
  formula: string;
  description: string;
  format: string;
  components: string[];
}

type KpiDefinitionsMap = Record<string, KpiDefinition>;

let _cache: KpiDefinitionsMap | null = null;
let _fetching: Promise<KpiDefinitionsMap> | null = null;

function fetchDefinitions(projectId: string): Promise<KpiDefinitionsMap> {
  if (_cache) return Promise.resolve(_cache);
  if (_fetching) return _fetching;

  _fetching = fetch(apiUrl('/api/kpi-definitions', projectId))
    .then(r => r.json())
    .then((data: KpiDefinitionsMap) => {
      _cache = data;
      _fetching = null;
      return data;
    })
    .catch(() => {
      _fetching = null;
      return {} as KpiDefinitionsMap;
    });
  return _fetching;
}

export function useKpiDefinitions(projectId: string): KpiDefinitionsMap | null {
  const [defs, setDefs] = useState<KpiDefinitionsMap | null>(_cache);

  useEffect(() => {
    if (_cache) { setDefs(_cache); return; }
    fetchDefinitions(projectId).then(setDefs);
  }, [projectId]);

  return defs;
}
